// Sticky add to cart: shows a bottom bar once the main add to cart button has scrolled out of
// view above the sticky header, and scrolls back to the purchase options when clicked.
// Landing page mode (data-mode="outside"): the bar shows whenever the whole product section is out of
// view, above or below, except while the landing hero's own button row is on screen.
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        // The quick add modal renders the same section; the bar only belongs on the page itself.
        if (this.closest('quick-add-modal')) return;

        const sectionId = this.dataset.sectionId;
        this.submit = document.getElementById(`ProductSubmitButton-${sectionId}`);
        this.info = document.getElementById(`ProductInfo-${sectionId}`);
        this.section = this.closest('product-info');
        this.heroButtons = document.querySelector('.mvlp-hero .mvlp-buy');
        if (!this.submit) return;

        this.price = this.querySelector('[data-sticky-atc-price]');
        this.querySelector('[data-sticky-atc-button]').addEventListener('click', () => this.scrollToPurchase());

        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            this.update();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });

        // Reserve room at the bottom of the page so the bar never covers the end of the footer.
        document.body.classList.add('has-mv-sticky-atc');
        this.resizeObserver = new ResizeObserver(() => {
          document.documentElement.style.setProperty('--mv-sticky-atc-height', `${this.offsetHeight}px`);
        });
        this.resizeObserver.observe(this);

        if (typeof subscribe === 'function') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data?.sectionId === sectionId) setTimeout(() => this.syncPrice());
          });
        }

        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.resizeObserver?.disconnect();
        this.unsubscribe?.();
        document.body.classList.remove('has-mv-sticky-atc');
      }

      // Copy the price Dawn renders for the selected variant, so both always match.
      syncPrice() {
        const price = document.getElementById(`price-${this.dataset.sectionId}`);
        const current =
          price?.querySelector('.price--on-sale .price-item--sale') ||
          price?.querySelector('.price__regular .price-item--regular');
        if (current && this.price) this.price.textContent = current.textContent.trim();
      }

      headerOffset() {
        return parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--mv-sticky-header-offset')) || 0;
      }

      inView(element) {
        if (!element || element.getClientRects().length === 0) return false;
        const rect = element.getBoundingClientRect();
        return rect.bottom > this.headerOffset() && rect.top < window.innerHeight;
      }

      update() {
        let visible;
        if (this.dataset.mode === 'outside') {
          visible = !this.inView(this.section) && !this.inView(this.heroButtons);
        } else {
          // Visible only once the button has passed above the header, never while it is still below.
          visible = this.submit.getClientRects().length > 0 && this.submit.getBoundingClientRect().bottom < this.headerOffset();
        }
        if (visible === this.visible) return;
        this.visible = visible;
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
        this.toggleAttribute('inert', !visible);
      }

      scrollToPurchase() {
        const target =
          this.info?.querySelector('.shopify-app-block[id*="kaching"]') ||
          this.info?.querySelector('variant-selects, .product-form__input') ||
          this.submit;
        const top = target.getBoundingClientRect().top + window.scrollY - this.headerOffset() - 24;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });

        // Keyboard and screen reader users land on the first purchase control.
        // Apps like Kaching hide their radios and use role="button" elements, so take the first
        // control that is actually rendered.
        const controls = target.querySelectorAll('input:not([type="hidden"]), button, select, [tabindex]:not([tabindex="-1"])');
        const focusTarget = [...controls].find((control) => control.getClientRects().length > 0) || this.submit;

        // Move focus only once the scroll has finished: changing focus during a smooth scroll can
        // cancel the animation and make the page jump.
        let focused = false;
        const focus = () => {
          if (focused) return;
          focused = true;
          focusTarget.focus({ preventScroll: true });
        };
        if (reduceMotion) {
          focus();
        } else {
          window.addEventListener('scrollend', focus, { once: true });
          setTimeout(focus, 1200);
        }
      }
    }
  );
}
